import React, { useState, useEffect } from 'react';
import { Controller, Control, FieldErrors, UseFormWatch, UseFormSetValue, useFieldArray } from 'react-hook-form';
import { PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { FileUploadInput } from '@/components/shared';
import { useFileUpload } from '@/hooks/form-handlers/useFileUpload';

interface MedicalCertificatesFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: Control<CollaboratorFormData>;
    setValue: UseFormSetValue<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
}

const MedicalCertificatesFieldset: React.FC<MedicalCertificatesFieldsetProps> = ({
    context,
    collaboratorId,
    control,
    setValue,
    watch,
    errors,
    isSubmitting = false
}) => {
    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Estado local para os dados do formulário (necessário para o useFileUpload)
    const [formData, setFormData] = useState<CollaboratorFormData>({} as CollaboratorFormData);

    // File upload hook
    const { uploadStates, handleFileChange } = useFileUpload(setFormData);

    // Sincronizar mudanças do React Hook Form com o estado local
    useEffect(() => {
        const subscription = watch((value) => {
            setFormData(value as CollaboratorFormData);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    // useEffect para sincronizar URLs dos certificados médicos
    useEffect(() => {
        if (formData.medicalCertificates) {
            formData.medicalCertificates.forEach((cert, index) => {
                if (cert.certificateUrl) {
                    const currentUrl = watch(`medicalCertificates.${index}.certificateUrl`);
                    if (currentUrl !== cert.certificateUrl) {
                        setValue(`medicalCertificates.${index}.certificateUrl`, cert.certificateUrl);
                    }
                }
            });
        }
    }, [formData.medicalCertificates, setValue, watch]);

    // Helper local para obter layout contextual
    const getLayoutByContext = (context: CollaboratorFormContext) => {
        switch (context) {
            case 'DP_UPDATE':
            case 'DHO_UPDATE':
                return {
                    fieldsetStyle: 'border p-4 rounded-md',
                    legendStyle: 'text-sm font-medium px-1 text-gray-400'
                };
            default: // FULL_FORM
                return {
                    fieldsetStyle: 'border p-4 rounded-md',
                    legendStyle: 'text-sm font-medium px-1 text-gray-400'
                };
        }
    };

    const layout = getLayoutByContext(context);

    // useFieldArray para gerenciar certificados médicos
    const { fields, append, remove } = useFieldArray({
        control,
        name: "medicalCertificates"
    });

    // Verificar se o fieldset deve ser exibido
    if (!shouldShow('medicalCertificates')) {
        return null;
    }

    const handleFileUpload = (index: number) => async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        await handleFileChange(e, {
            fieldName: `medicalCertificates[${index}].certificateFile`,
            urlFieldName: `medicalCertificates[${index}].certificateUrl`,
            pathPrefix: 'medical-certificates',
            id: collaboratorId
        });
    };

    const handleAddCertificate = () => {
        append({
            id: '',
            date: '',
            cid: '',
            days: 1,
            certificateFile: undefined,
            certificateUrl: ''
        });
    };

    return (
        <fieldset className={layout.fieldsetStyle}>
            <legend className={layout.legendStyle}>Atestados Médicos</legend>
            <div className="space-y-4 pt-2">
                {fields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center border-b pb-4">
                        {/* Data do Atestado */}
                        <div className="md:col-span-2">
                            <label htmlFor={`cert-date-${index}`} className="block text-sm font-medium text-gray-700">
                                Data do Atestado
                            </label>
                            <Controller
                                name={`medicalCertificates.${index}.date`}
                                control={control}
                                render={({ field: dateField, fieldState }) => (
                                    <>
                                        <input
                                            {...dateField}
                                            type="date"
                                            id={`cert-date-${index}`}
                                            className="input-primary w-full mt-1"
                                            disabled={isSubmitting}
                                        />
                                        {fieldState.error && (
                                            <p className="mt-1 text-sm text-red-600" role="alert">
                                                {fieldState.error.message}
                                            </p>
                                        )}
                                    </>
                                )}
                            />
                        </div>

                        {/* CID */}
                        <div className="md:col-span-2">
                            <label htmlFor={`cert-cid-${index}`} className="block text-sm font-medium text-gray-700">
                                CID
                            </label>
                            <Controller
                                name={`medicalCertificates.${index}.cid`}
                                control={control}
                                render={({ field: cidField, fieldState }) => (
                                    <>
                                        <input
                                            {...cidField}
                                            type="text"
                                            id={`cert-cid-${index}`}
                                            className="input-primary w-full mt-1"
                                            disabled={isSubmitting}
                                        />
                                        {fieldState.error && (
                                            <p className="mt-1 text-sm text-red-600" role="alert">
                                                {fieldState.error.message}
                                            </p>
                                        )}
                                    </>
                                )}
                            />
                        </div>

                        {/* Dias */}
                        <div className="md:col-span-1">
                            <label htmlFor={`cert-days-${index}`} className="block text-sm font-medium text-gray-700">
                                Dias
                            </label>
                            <Controller
                                name={`medicalCertificates.${index}.days`}
                                control={control}
                                render={({ field: daysField, fieldState }) => (
                                    <>
                                        <input
                                            type="number"
                                            id={`cert-days-${index}`}
                                            value={daysField.value || ''}
                                            onChange={(e) => daysField.onChange(parseInt(e.target.value, 10) || 0)}
                                            onBlur={daysField.onBlur}
                                            className="input-primary w-full mt-1"
                                            disabled={isSubmitting}
                                        />
                                        {fieldState.error && (
                                            <p className="mt-1 text-sm text-red-600" role="alert">
                                                {fieldState.error.message}
                                            </p>
                                        )}
                                    </>
                                )}
                            />
                        </div>

                        {/* Upload de Arquivo e Botão Remover */}
                        <div className="flex items-end space-x-2 md:col-span-7">
                            <div className="flex-grow">
                                <Controller
                                    name={`medicalCertificates.${index}.certificateFile`}
                                    control={control}
                                    render={({ field: fileField, fieldState }) => (
                                        <div>
                                            <FileUploadInput
                                                name={`certificateFile-${index}`}
                                                label="Anexar Atestado"
                                                fileUrl={watch(`medicalCertificates.${index}.certificateUrl`) || ''}
                                                fileName={fileField.value?.name || ''}
                                                isSubmitting={isSubmitting}
                                                onChange={handleFileUpload(index)}
                                                progress={uploadStates[`medicalCertificates[${index}].certificateFile`]?.progress}
                                                status={uploadStates[`medicalCertificates[${index}].certificateFile`]?.status || 'idle'}
                                            />
                                            {fieldState.error && (
                                                <p className="mt-1 text-sm text-red-600" role="alert">
                                                    {fieldState.error.message}
                                                </p>
                                            )}
                                            {/* Controller oculto para certificateUrl */}
                                            <Controller
                                                name={`medicalCertificates.${index}.certificateUrl`}
                                                control={control}
                                                render={({ field }) => (
                                                    <input type="hidden" {...field} value={field.value || ''} />
                                                )}
                                            />
                                        </div>
                                    )}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => remove(index)}
                                className="btn-danger p-2 self-end mb-1"
                                disabled={isSubmitting}
                            >
                                <TrashIcon className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                ))}

                <div className="flex justify-start">
                    <button
                        type="button"
                        onClick={handleAddCertificate}
                        className="flex items-center gap-2 px-4 py-2 text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50 transition-colors"
                        disabled={isSubmitting}
                    >
                        <PlusCircleIcon className="h-5 w-5 mr-2" />
                        Adicionar Atestado
                    </button>
                </div>

                {errors.medicalCertificates && (
                    <p className="mt-2 text-sm text-red-600" role="alert">
                        {errors.medicalCertificates.message}
                    </p>
                )}
            </div>
        </fieldset>
    );
};

export default MedicalCertificatesFieldset; 